/**
 * A small, fast, seeded pseudo-random generator (mulberry32).
 *
 * `Math.random` cannot be seeded, and the asteroid field must be reproducible:
 * the same seed has to produce the same rocks in the same order on every
 * machine. That is what lets two players share a field over the network by
 * exchanging a single number instead of streaming positions, and what makes
 * the spawner testable at all.
 */
export interface Rng {
  /** Next float in [0, 1). */
  next(): number;
  /** Float in [min, max). */
  range(min: number, max: number): number;
  /** Current internal state, so a field can be resumed or replayed. */
  readonly state: number;
}

export function createRng(seed: number): Rng {
  let state = Math.trunc(seed) >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  return {
    next,
    range: (min, max) => min + next() * (max - min),
    get state() {
      return state;
    },
  };
}

/** A seed for a fresh run. Only used where reproducibility is not required. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}
